import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BookOpenIcon, CheckIcon, ImageIcon, SaveIcon, TagIcon, CalendarIcon, ClockIcon, AlertCircleIcon, EyeIcon, MenuIcon, XIcon, FeatherIcon, LogOutIcon, HomeIcon, TrashIcon } from 'lucide-react';
interface BlogPost {
  id: string;
  title: string;
  content: string;
  category: string;
  excerpt: string;
  featuredImage: string;
  metaTitle: string;
  metaDescription: string;
  slug: string;
  date: string;
  author: string;
  status: string;
}
// Mock data for a specific post
const mockPost: BlogPost = {
  id: '1',
  title: 'The Art of Brand Storytelling in East Africa',
  content: "East African brands have a unique opportunity to connect with audiences through authentic storytelling. This post explores how local context, cultural insights, and narrative techniques can be leveraged to create brand stories that resonate deeply with East African consumers.\n\nWhen crafting brand narratives for the East African market, it's essential to understand the diverse cultural landscape, historical contexts, and contemporary aspirations of your audience. Effective storytelling goes beyond mere translation—it requires cultural adaptation and nuanced understanding of local values.\n\nIn this article, we'll explore practical approaches to developing brand stories that connect emotionally with East African audiences while driving business results.",
  category: 'Brand Strategy',
  excerpt: 'Discover how to leverage cultural insights and narrative techniques to create brand stories that resonate with East African audiences.',
  featuredImage: 'https://images.unsplash.com/photo-1503551723145-6c040742065b',
  metaTitle: 'The Art of Brand Storytelling in East Africa | Effective Techniques',
  metaDescription: 'Learn how to create authentic brand narratives that resonate with East African audiences through cultural insights and storytelling techniques.',
  slug: 'the-art-of-brand-storytelling-in-east-africa',
  date: 'June 12, 2023',
  author: 'Kibaki Muthamia',
  status: 'published'
};
export function AdminBlogEdit() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('content');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    // In a real app, fetch the post from your API
    // For demo purposes, we'll use mock data
    try {
      // Simulate API call
      setTimeout(() => {
        setPost(mockPost);
        setTitle(mockPost.title);
        setContent(mockPost.content);
        setCategory(mockPost.category);
        setExcerpt(mockPost.excerpt);
        setFeaturedImage(mockPost.featuredImage);
        setMetaTitle(mockPost.metaTitle);
        setMetaDescription(mockPost.metaDescription);
        setSlug(mockPost.slug);
      }, 500);
    } catch (err) {
      setError('Failed to load post. Please try again.');
    }
  }, [id]);
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };
  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveSuccess(false);
    setError('');
    try {
      // For demo purposes, we'll just simulate saving
      await new Promise(resolve => setTimeout(resolve, 1500));
      // In a real app, you would update the post via your API
      // const response = await fetch(`http://localhost:3001/posts/${id}`, {
      //   method: 'PUT',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     title,
      //     content,
      //     category,
      //     excerpt,
      //     featuredImage,
      //     metaTitle,
      //     metaDescription,
      //     slug,
      //     date: new Date().toISOString(),
      //     author: post?.author || localStorage.getItem('userName') || 'Admin'
      //   })
      // })
      setSaveSuccess(true);
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err) {
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setIsDeleting(true);
    try {
      // For demo purposes, we'll just simulate deleting
      await new Promise(resolve => setTimeout(resolve, 1500));
      // In a real app, you would delete the post via your API
      // await fetch(`http://localhost:3001/posts/${id}`, {
      //   method: 'DELETE'
      // })
      navigate('/admin');
    } catch (err) {
      setError('Failed to delete post. Please try again.');
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userName');
    navigate('/admin/login');
  };
  const handlePreview = () => {
    // Open preview in new tab
    const previewWindow = window.open(`/blog/${slug || id}`, '_blank');
    if (previewWindow) previewWindow.focus();
  };
  if (!post && !error) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-364">
        <div className="w-16 h-16 border-t-4 border-amber-600 border-solid rounded-full animate-spin" data-id="element-365"></div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 flex" data-id="element-366">
      {/* Sidebar - Desktop */}
      <div className="hidden md:flex flex-col w-64 bg-gray-800 text-white" data-id="element-367">
        <div className="p-5 flex items-center border-b border-gray-700" data-id="element-368">
          <FeatherIcon size={24} className="text-amber-500 mr-3" data-id="element-369" />
          <h1 className="text-xl font-bold" data-id="element-370">Muthamia Admin</h1>
        </div>
        <nav className="flex-1 overflow-y-auto py-4" data-id="element-371">
          <Link to="/admin" className="flex items-center px-4 py-3 text-gray-300 hover:bg-gray-700" data-id="element-372">
            <HomeIcon size={18} className="mr-3" data-id="element-373" />
            <span data-id="element-374">Dashboard</span>
          </Link>
          <Link to="/admin" className="flex items-center px-4 py-3 bg-gray-700 text-amber-400" data-id="element-375">
            <BookOpenIcon size={18} className="mr-3" data-id="element-376" />
            <span data-id="element-377">Posts</span>
          </Link>
        </nav>
        <div className="p-4 border-t border-gray-700" data-id="element-378">
          <button onClick={handleLogout} className="flex items-center text-gray-300 hover:text-white w-full" data-id="element-379">
            <LogOutIcon size={18} className="mr-3" data-id="element-380" />
            <span data-id="element-381">Logout</span>
          </button>
        </div>
      </div>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-gray-800 text-white p-4 flex justify-between items-center" data-id="element-382">
        <div className="flex items-center" data-id="element-383">
          <FeatherIcon size={24} className="text-amber-500 mr-3" data-id="element-384" />
          <h1 className="text-lg font-bold" data-id="element-385">Muthamia Admin</h1>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white focus:outline-none" data-id="element-386">
          {sidebarOpen ? <XIcon size={24} data-id="element-387" /> : <MenuIcon size={24} data-id="element-388" />}
        </button>
      </div>

      {/* Mobile sidebar */}
      {sidebarOpen && <div className="md:hidden fixed inset-0 z-30 bg-gray-800 pt-16 overflow-y-auto" data-id="element-389">
          <nav className="py-4" data-id="element-390">
            <Link to="/admin" className="flex items-center px-4 py-3 text-gray-300" onClick={() => setSidebarOpen(false)} data-id="element-391">
              <HomeIcon size={18} className="mr-3" data-id="element-392" />
              <span data-id="element-393">Dashboard</span>
            </Link>
            <Link to="/admin" className="flex items-center px-4 py-3 bg-gray-700 text-amber-400" onClick={() => setSidebarOpen(false)} data-id="element-394">
              <BookOpenIcon size={18} className="mr-3" data-id="element-395" />
              <span data-id="element-396">Posts</span>
            </Link>
            <div className="px-4 mt-6 pt-6 border-t border-gray-700" data-id="element-397">
              <button onClick={handleLogout} className="flex items-center text-gray-300 hover:text-white w-full py-3" data-id="element-398">
                <LogOutIcon size={18} className="mr-3" data-id="element-399" />
                <span data-id="element-400">Logout</span>
              </button>
            </div>
          </nav>
        </div>}

      {/* Main Content */}
      <div className="flex-1 md:ml-64 md:mt-0 mt-16" data-id="element-401">
        <div className="py-6 px-4 sm:px-6 lg:px-8" data-id="element-402">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8" data-id="element-403">
            <div data-id="element-404">
              <div className="flex items-center" data-id="element-405">
                <Link to="/admin" className="mr-4 text-gray-500 hover:text-gray-700" data-id="element-406">
                  <ArrowLeftIcon size={20} data-id="element-407" />
                </Link>
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-408">Edit Post</h1>
              </div>
              <p className="mt-1 text-sm text-gray-500" data-id="element-409">
                Last updated: {post?.date || 'Unknown'}
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-3" data-id="element-410">
              <button type="button" onClick={handlePreview} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500" data-id="element-411">
                <EyeIcon size={16} className="mr-2" data-id="element-412" />
                Preview
              </button>
              <button type="button" onClick={handleSubmit} disabled={isSaving} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50" data-id="element-413">
                {isSaving ? <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" data-id="element-414"></span>
                    Saving...
                  </> : <>
                    <SaveIcon size={16} className="mr-2" data-id="element-415" />
                    Save Changes
                  </>}
              </button>
            </div>
          </div>

          {saveSuccess && <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-md flex items-center" data-id="element-416">
              <CheckIcon size={20} className="mr-2" data-id="element-417" />
              <span data-id="element-418">Post updated successfully!</span>
            </div>}

          {error && <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md flex items-center" data-id="element-419">
              <AlertCircleIcon size={20} className="mr-2" data-id="element-420" />
              <span data-id="element-421">{error}</span>
            </div>}

          <div className="bg-white shadow-md rounded-lg overflow-hidden" data-id="element-422">
            {/* Post Title */}
            <div className="p-6 border-b border-gray-200" data-id="element-423">
              <input type="text" value={title} onChange={handleTitleChange} placeholder="Post Title" className="w-full text-3xl font-bold border-0 focus:ring-0 focus:outline-none placeholder-gray-300" data-id="element-424" />
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200" data-id="element-425">
              <nav className="flex" data-id="element-426">
                <button onClick={() => setActiveTab('content')} className={`px-6 py-4 text-sm font-medium ${activeTab === 'content' ? 'border-b-2 border-amber-500 text-amber-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-427">
                  Content
                </button>
                <button onClick={() => setActiveTab('seo')} className={`px-6 py-4 text-sm font-medium ${activeTab === 'seo' ? 'border-b-2 border-amber-500 text-amber-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-428">
                  SEO
                </button>
                <button onClick={() => setActiveTab('settings')} className={`px-6 py-4 text-sm font-medium ${activeTab === 'settings' ? 'border-b-2 border-amber-500 text-amber-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-429">
                  Settings
                </button>
              </nav>
            </div>

            {/* Content Tab */}
            {activeTab === 'content' && <div className="p-6" data-id="element-430">
                <div className="mb-6" data-id="element-431">
                  <div className="flex justify-between items-center mb-2" data-id="element-432">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-433">
                      Featured Image
                    </label>
                    <button type="button" className="text-xs text-amber-600 hover:text-amber-700" data-id="element-434">
                      Choose from Media Library
                    </button>
                  </div>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-435">
                    {featuredImage ? <div className="relative" data-id="element-436">
                        <img src={featuredImage} alt="Featured" className="max-h-48 mx-auto" data-id="element-437" />
                        <button type="button" onClick={() => setFeaturedImage('')} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600" data-id="element-438">
                          <XIcon size={16} data-id="element-439" />
                        </button>
                      </div> : <>
                        <ImageIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-440" />
                        <p className="text-sm text-gray-500 mb-4" data-id="element-441">
                          Drag and drop an image here or click to upload
                        </p>
                        <input type="text" placeholder="Or paste an image URL" value={featuredImage} onChange={e => setFeaturedImage(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-442" />
                      </>}
                  </div>
                </div>

                <div className="mb-6" data-id="element-443">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-444">
                    Content
                  </label>
                  <textarea value={content} onChange={handleContentChange} rows={15} className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="Write your post content here..." data-id="element-445"></textarea>
                  <p className="mt-2 text-sm text-gray-500" data-id="element-446">
                    Tip: You can use Markdown formatting for headings, lists,
                    and more.
                  </p>
                </div>

                <div className="mb-6" data-id="element-447">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-448">
                    Excerpt
                  </label>
                  <textarea value={excerpt} onChange={e => setExcerpt(e.target.value)} rows={3} className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="Write a short excerpt for your post..." data-id="element-449"></textarea>
                  <p className="mt-2 text-sm text-gray-500" data-id="element-450">
                    A short summary of your post that will appear on the blog
                    index page.
                  </p>
                </div>
              </div>}

            {/* SEO Tab */}
            {activeTab === 'seo' && <div className="p-6" data-id="element-451">
                <div className="mb-6" data-id="element-452">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-453">
                    Slug
                  </label>
                  <div className="flex items-center" data-id="element-454">
                    <span className="text-gray-500 mr-2" data-id="element-455">/blog/</span>
                    <input type="text" value={slug} onChange={e => setSlug(e.target.value)} className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="post-slug" data-id="element-456" />
                  </div>
                  <p className="mt-2 text-sm text-gray-500" data-id="element-457">
                    The URL-friendly version of the post title.
                  </p>
                </div>

                <div className="mb-6" data-id="element-458">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-459">
                    Meta Title
                  </label>
                  <input type="text" value={metaTitle} onChange={e => setMetaTitle(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="SEO title (appears in search results)" data-id="element-460" />
                  <div className="mt-2 flex justify-between" data-id="element-461">
                    <p className="text-sm text-gray-500" data-id="element-462">
                      Recommended length: 50-60 characters
                    </p>
                    <p className={`text-sm ${metaTitle.length > 60 ? 'text-red-500' : 'text-gray-500'}`} data-id="element-463">
                      {metaTitle.length}/60
                    </p>
                  </div>
                </div>

                <div className="mb-6" data-id="element-464">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-465">
                    Meta Description
                  </label>
                  <textarea value={metaDescription} onChange={e => setMetaDescription(e.target.value)} rows={3} className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="SEO description (appears in search results)" data-id="element-466"></textarea>
                  <div className="mt-2 flex justify-between" data-id="element-467">
                    <p className="text-sm text-gray-500" data-id="element-468">
                      Recommended length: 150-160 characters
                    </p>
                    <p className={`text-sm ${metaDescription.length > 160 ? 'text-red-500' : 'text-gray-500'}`} data-id="element-469">
                      {metaDescription.length}/160
                    </p>
                  </div>
                </div>

                <div className="p-4 border border-gray-200 rounded-md bg-gray-50 mb-6" data-id="element-470">
                  <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-471">
                    Search Engine Preview
                  </h3>
                  <div className="p-4 bg-white border border-gray-200 rounded-md" data-id="element-472">
                    <p className="text-blue-600 text-lg font-medium truncate" data-id="element-473">
                      {metaTitle || title}
                    </p>
                    <p className="text-green-700 text-sm" data-id="element-474">
                      www.muthamiacontent.com/blog/{slug}
                    </p>
                    <p className="text-gray-600 text-sm mt-1 line-clamp-2" data-id="element-475">
                      {metaDescription || excerpt}
                    </p>
                  </div>
                </div>
              </div>}

            {/* Settings Tab */}
            {activeTab === 'settings' && <div className="p-6" data-id="element-476">
                <div className="mb-6" data-id="element-477">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-478">
                    Category
                  </label>
                  <select value={category} onChange={e => setCategory(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" data-id="element-479">
                    <option value="" data-id="element-480">Select a category</option>
                    <option value="Business" data-id="element-481">Business</option>
                    <option value="News" data-id="element-482">News</option>
                    <option value="Sports" data-id="element-483">Sports</option>
                    <option value="Storytelling" data-id="element-484">Storytelling</option>
                  </select>
                </div>

                <div className="mb-6" data-id="element-485">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-486">
                    Tags
                  </label>
                  <div className="flex items-center" data-id="element-487">
                    <TagIcon size={16} className="text-gray-400 mr-2" data-id="element-488" />
                    <input type="text" defaultValue="brand storytelling, east africa, marketing" className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="Add tags separated by commas" data-id="element-489" />
                  </div>
                  <p className="mt-2 text-sm text-gray-500" data-id="element-490">
                    Tags help categorize your content and improve
                    discoverability.
                  </p>
                </div>

                <div className="mb-6" data-id="element-491">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-492">
                    Publication Date
                  </label>
                  <div className="flex items-center" data-id="element-493">
                    <CalendarIcon size={16} className="text-gray-400 mr-2" data-id="element-494" />
                    <input type="date" defaultValue="2023-06-12" className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" data-id="element-495" />
                    <ClockIcon size={16} className="text-gray-400 ml-4 mr-2" data-id="element-496" />
                    <input type="time" defaultValue="09:00" className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" data-id="element-497" />
                  </div>
                </div>

                <div className="mb-6" data-id="element-498">
                  <div className="flex items-center" data-id="element-499">
                    <input id="allow-comments" type="checkbox" className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded" defaultChecked data-id="element-500" />
                    <label htmlFor="allow-comments" className="ml-2 block text-sm text-gray-700" data-id="element-501">
                      Allow comments on this post
                    </label>
                  </div>
                </div>

                <div className="mb-6" data-id="element-502">
                  <div className="flex items-center" data-id="element-503">
                    <input id="featured-post" type="checkbox" className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded" defaultChecked data-id="element-504" />
                    <label htmlFor="featured-post" className="ml-2 block text-sm text-gray-700" data-id="element-505">
                      Mark as featured post
                    </label>
                  </div>
                  <p className="mt-2 text-sm text-gray-500 ml-6" data-id="element-506">
                    Featured posts appear prominently on your homepage.
                  </p>
                </div>

                <div className="border-t border-gray-200 pt-6 mt-6" data-id="element-507">
                  <h3 className="text-sm font-medium text-gray-700 mb-4" data-id="element-508">
                    Danger Zone
                  </h3>
                  <div className="bg-red-50 border border-red-200 rounded-md p-4" data-id="element-509">
                    <div className="flex justify-between items-center" data-id="element-510">
                      <div data-id="element-511">
                        <h4 className="text-sm font-medium text-red-800" data-id="element-512">
                          Delete this post
                        </h4>
                        <p className="text-sm text-red-700 mt-1" data-id="element-513">
                          Once deleted, this post cannot be recovered.
                        </p>
                      </div>
                      {confirmDelete ? <div className="flex items-center" data-id="element-514">
                          <span className="text-sm text-red-800 mr-2" data-id="element-515">
                            Are you sure?
                          </span>
                          <button type="button" onClick={handleDelete} disabled={isDeleting} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-516">
                            {isDeleting ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" data-id="element-517"></span> : 'Yes, delete'}
                          </button>
                          <button type="button" onClick={() => setConfirmDelete(false)} className="ml-2 inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" data-id="element-518">
                            Cancel
                          </button>
                        </div> : <button type="button" onClick={handleDelete} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-519">
                          <TrashIcon size={14} className="mr-1" data-id="element-520" />
                          Delete
                        </button>}
                    </div>
                  </div>
                </div>
              </div>}
          </div>

          <div className="mt-6 flex justify-end space-x-3" data-id="element-521">
            <Link to="/admin" className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500" data-id="element-522">
              Cancel
            </Link>
            <button type="button" onClick={handleSubmit} disabled={isSaving} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50" data-id="element-523">
              {isSaving ? <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" data-id="element-524"></span>
                  Saving...
                </> : <>
                  <SaveIcon size={16} className="mr-2" data-id="element-525" />
                  Save Changes
                </>}
            </button>
          </div>
        </div>
      </div>
    </div>;
}